import "./ResizePlugin.scss";
import { I18n, defaultLocale, type Locale } from "./i18n";
import { format } from "./utils";

interface Size {
    width: number;
    height: number;
}

interface Position {
    left: number;
    top: number;
    width: number;
    height: number;
}

class ResizeElement extends HTMLElement {
    public originSize?: Size | null = null;
    [key: string]: any;
}

interface ResizePluginOption {
    locale?: Locale;
    [index: string]: any;
    keepAspectRatio?: boolean;
    readOnly?: boolean;
    onImageClick?: (img: HTMLImageElement) => void;
    onResizeEnd?: (img: HTMLImageElement) => void;
    resizeConstraints?: {
        minWidth?: number;
        maxWidth?: number;
        minHeight?: number;
        maxHeight?: number;
    };
}

const GEAR_SVG = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/></svg>`;

const template = `
<div class="settings-btn" title="{1}">${GEAR_SVG}</div>
<div class="size-badge"></div>
<div class="handler" title="{0}"></div>
<div class="toolbar">
  <div class="group">
    <a class="btn" data-type="width" data-styles="width:100%">100%</a>
    <a class="btn" data-type="width" data-styles="width:50%">50%</a>
    <span class="input-wrapper"><input data-type="width" maxlength="3" /><span class="suffix">%</span><span class="tooltip">{6}</span></span>
    <a class="btn" data-type="width" data-styles="width:auto">{5}</a>
  </div>
  <div class="group">
    <a class="btn" data-type="align" data-styles="float:left">{2}</a>
    <a class="btn" data-type="align" data-styles="display:block;margin:auto;">{3}</a>
    <a class="btn" data-type="align" data-styles="float:right;">{4}</a>
    <a class="btn" data-type="align" data-styles="">{5}</a>
  </div>
  <div class="group readout">
    <span class="readout-label">Size</span>
    <span class="readout-size">&mdash;</span>
  </div>
</div>
`;

class ResizePlugin {
    resizeTarget: ResizeElement;
    resizer: HTMLElement | null = null;
    container: HTMLElement;
    editor: HTMLElement;
    startResizePosition: Position | null = null;
    i18n: I18n;
    options: any;
    private hideTimer: number | null = null;
    private resizeMoved = false;

    constructor(
        resizeTarget: HTMLElement,
        container: HTMLElement,
        editor: HTMLElement,
        options?: ResizePluginOption
    ) {
        this.i18n = new I18n(options?.locale || defaultLocale);
        this.options = options;
        this.resizeTarget = resizeTarget as ResizeElement;
        if (!this.resizeTarget.originSize) {
            this.resizeTarget.originSize = {
                width: this.resizeTarget.clientWidth,
                height: this.resizeTarget.clientHeight,
            };
        }
        this.editor = editor;
        this.container = container;
        this.initResizer();
        this.positionResizerToTarget(resizeTarget);
        if (options?.readOnly) {
            this.resizer?.classList.add("readonly");
        }

        this.resizing = this.resizing.bind(this);
        this.endResize = this.endResize.bind(this);
        this.startResize = this.startResize.bind(this);
        this.onResizerClick = this.onResizerClick.bind(this);
        this.toolbarInputChange = this.toolbarInputChange.bind(this);
        this.onScroll = this.onScroll.bind(this);
        this.onResizerEnter = this.onResizerEnter.bind(this);
        this.onResizerLeave = this.onResizerLeave.bind(this);
        this.onResizerWheel = this.onResizerWheel.bind(this);
        this.bindEvents();
    }

    initResizer() {
        let resizer: HTMLElement | null =
            this.container.querySelector("#editor-resizer");
        if (!resizer) {
            resizer = document.createElement("div");
            resizer.setAttribute("id", "editor-resizer");
            resizer.innerHTML = format(
                template,
                this.i18n.findLabel("altTip"),
                this.i18n.findLabel("settingsTip"),
                this.i18n.findLabel("floatLeft"),
                this.i18n.findLabel("center"),
                this.i18n.findLabel("floatRight"),
                this.i18n.findLabel("restore"),
                this.i18n.findLabel("inputTip")
            );
            this.container.appendChild(resizer);
        }
        this.resizer = resizer;
    }

    positionResizerToTarget(el: HTMLElement) {
        if (!this.resizer || !this.container) return;

        // Use getBoundingClientRect to get the latest position relative to viewport
        const elRect = el.getBoundingClientRect();
        // The resizer's containing block is `.ql-container` (position: relative),
        // not the centered `.ql-editor` — measure against the real offset parent.
        const containerRect = this.container.getBoundingClientRect();

        const left = elRect.left - containerRect.left;
        const top = elRect.top - containerRect.top;

        // Update resizer position and size
        this.resizer.style.left = `${left}px`;
        this.resizer.style.top = `${top}px`;
        this.resizer.style.width = `${el.clientWidth}px`;
        this.resizer.style.height = `${el.clientHeight}px`;

        // Update the live size readout in the settings panel
        const readout = this.resizer.querySelector(".readout-size");
        if (readout) {
            readout.textContent = `${el.clientWidth} × ${el.clientHeight}`;
        }

        // Update the size badge shown over the image while dragging
        const badge = this.resizer.querySelector(".size-badge");
        if (badge) {
            badge.textContent = `${el.clientWidth}x${el.clientHeight}`;
        }

        // Preserve the image's aspect ratio when the content column is narrower
        // than the image. Editor images carry explicit width/height attributes and
        // `height: auto` (see app.scss), so the height would otherwise fall back to
        // the intrinsic ratio and discard any free-style (Ctrl) ratio the user set.
        if (el.tagName.toLowerCase() === "img") {
            const w = el.getAttribute("width");
            const h = el.getAttribute("height");
            if (w && h) {
                el.style.aspectRatio = `${w} / ${h}`;
            }
        }
    }

    bindEvents() {
        if (this.resizer !== null) {
            this.resizer.addEventListener("mousedown", this.startResize);
            this.resizer.addEventListener("click", this.onResizerClick);
            this.resizer.addEventListener("change", this.toolbarInputChange);
            this.resizer.addEventListener("mouseenter", this.onResizerEnter);
            this.resizer.addEventListener("mouseleave", this.onResizerLeave);
            // The resizer is a sibling of the scrollable editor (not a child), so a
            // wheel event over an image targets the resizer and the browser can't
            // find a scrollable ancestor. Forward the delta to the editor so the
            // image never blocks page scrolling.
            this.resizer.addEventListener("wheel", this.onResizerWheel, { passive: false });
        }
        window.addEventListener("mouseup", this.endResize);
        window.addEventListener("mousemove", this.resizing);
        this.editor.addEventListener("scroll", this.onScroll);
    }

    onScroll() {
        this.positionResizerToTarget(this.resizeTarget);
    }

    onResizerWheel(e: WheelEvent) {
        if (!this.editor) return;
        // Let a vertical scroll happen in the editor first; only swallow the
        // event if the editor actually consumed it. Otherwise (e.g. editor at
        // its scroll limits) fall through so the window can scroll instead.
        const before = this.editor.scrollTop;
        this.editor.scrollTop += e.deltaY;
        const consumed = this.editor.scrollTop !== before;
        if (consumed) {
            e.preventDefault();
        }
    }

    _setStylesForToolbar(type: string, styles: string | undefined) {
        const storeKey = `_styles_${type}`;
        const style: CSSStyleDeclaration = this.resizeTarget.style;
        const originStyles = this.resizeTarget[storeKey];
        style.cssText =
            style.cssText.replace(/ /g, "").replace(originStyles, "") +
            `;${styles}`;
        this.resizeTarget[storeKey] = styles;
        this.positionResizerToTarget(this.resizeTarget);
        this.options?.onChange(this.resizeTarget);
    }

    toolbarInputChange(e: Event) {
        const target: HTMLInputElement = e.target as HTMLInputElement;
        const type = target?.dataset?.type;
        const value = target.value;
        if (type && Number(value)) {
            this._setStylesForToolbar(type, `width: ${Number(value)}%;`);
        }
    }

    // ── Hover visibility ─────────────────────────────────────────────────────
    onResizerEnter() {
        this.show();
    }

    onResizerLeave() {
        this.scheduleHide();
    }

    show() {
        this.cancelHide();
        this.resizer?.classList.add("visible");
    }

    cancelHide() {
        if (this.hideTimer) {
            clearTimeout(this.hideTimer);
            this.hideTimer = null;
        }
    }

    scheduleHide() {
        this.cancelHide();
        this.hideTimer = window.setTimeout(() => {
            this.resizer?.classList.remove("visible");
            this.resizer?.classList.remove("toolbar-open");
            this.hideTimer = null;
        }, 150);
    }

    // ── Settings (gear) + click-to-fullscreen ────────────────────────────────
    toggleToolbar() {
        this.resizer?.classList.toggle("toolbar-open");
        this.cancelHide();
    }

    onResizerClick(e: MouseEvent) {
        const target = e.target as HTMLElement;

        // Ignore the click that follows a resize drag. After dragging the
        // corner handle the browser dispatches `click` on the resizer surface
        // (the common ancestor of the press/release points), which would
        // otherwise open the fullscreen viewer instead of finishing a resize.
        if (this.resizeMoved) {
            this.resizeMoved = false;
            return;
        }

        // Gear button → toggle the settings panel
        if (target.closest(".settings-btn")) {
            e.stopPropagation();
            this.toggleToolbar();
            return;
        }
        // Drag handle → never opens fullscreen
        if (target.closest(".handler")) {
            return;
        }
        // Inside the settings panel → handle config controls only
        if (target.closest(".toolbar")) {
            if (target.classList.contains("btn")) {
                const type = target.dataset.type;
                if (type) {
                    this._setStylesForToolbar(type, target.dataset.styles);
                }
            }
            return;
        }
        // Clicking the image body (the resizer surface) → open fullscreen
        if (this.options?.onImageClick && this.resizeTarget.tagName.toLowerCase() === "img") {
            this.options.onImageClick(this.resizeTarget as HTMLImageElement);
        }
    }

    startResize(e: MouseEvent) {
        const target: HTMLElement = e.target as HTMLElement;

        if (target.classList.contains("handler") && e.which === 1) {
            this.startResizePosition = {
                left: e.clientX,
                top: e.clientY,
                width: this.resizeTarget.clientWidth,
                height: this.resizeTarget.clientHeight,
            };
            this.resizer?.classList.add("resizing");
        }
    }

    endResize() {
        if (!this.startResizePosition) return;
        this.startResizePosition = null;
        this.resizer?.classList.remove("resizing");
        this.options?.onChange(this.resizeTarget);
        // Notify the host that a resize drag finished so it can re-request the
        // server-scaled variant matching the new width (only when the size
        // actually changed, to avoid a needless fetch on a no-op release).
        if (this.resizeMoved && this.resizeTarget.tagName.toLowerCase() === "img") {
            this.options?.onResizeEnd?.(this.resizeTarget as HTMLImageElement);
        }
        this.resizeMoved = false;
    }

    resizing(e: MouseEvent) {
        if (!this.startResizePosition) return;
        const deltaX: number = e.clientX - this.startResizePosition.left;
        const deltaY: number = e.clientY - this.startResizePosition.top;
        let width = this.startResizePosition.width;
        width += deltaX;

        // Clamp the width within the allowed bounds.
        const minWidth = this.options?.resizeConstraints?.minWidth ?? 30;
        const maxWidth = this.options?.resizeConstraints?.maxWidth;
        if (width < minWidth) {
            width = minWidth;
        }
        if (maxWidth !== undefined && width > maxWidth) {
            width = maxWidth;
        }

        // Default: keep the aspect ratio by deriving height from the ratio the
        // image had when the drag started. Holding Ctrl switches to free-style
        // resizing where width and height are driven independently.
        let height: number;
        if (e.ctrlKey) {
            height = this.startResizePosition.height + deltaY;
        } else {
            const rate: number =
                this.startResizePosition.height / this.startResizePosition.width;
            height = rate * width;
        }

        const minHeight = this.options?.resizeConstraints?.minHeight ?? 30;
        const maxHeight = this.options?.resizeConstraints?.maxHeight;
        if (height < minHeight) {
            height = minHeight;
        }
        if (maxHeight !== undefined && height > maxHeight) {
            height = maxHeight;
        }

        this.resizeTarget.setAttribute("width", `${width}`);
        this.resizeTarget.setAttribute("height", `${height}`);
        this.resizeMoved = true;
        this.positionResizerToTarget(this.resizeTarget);
    }

    destory() {
        this.cancelHide();
        if (this.resizer) {
            this.resizer.removeEventListener("mousedown", this.startResize);
            this.resizer.removeEventListener("click", this.onResizerClick);
            this.resizer.removeEventListener("change", this.toolbarInputChange);
            this.resizer.removeEventListener("mouseenter", this.onResizerEnter);
            this.resizer.removeEventListener("mouseleave", this.onResizerLeave);
        }
        this.container.removeChild(this.resizer as HTMLElement);
        window.removeEventListener("mouseup", this.endResize);
        window.removeEventListener("mousemove", this.resizing);
        this.editor.removeEventListener("scroll", this.onScroll);
        this.resizer = null;
    }
}

export default ResizePlugin;
